import { getFunctionParamType, getTagPillColor } from "components/CC/UI/TagInput/tagInputUtils";
import {
    $applyNodeReplacement,
    DecoratorNode,
    type LexicalNode,
    type NodeKey,
    type SerializedLexicalNode,
    type Spread,
} from "lexical";
import { useEffect, useState, type ReactNode } from "react";

// ============================================================================
// Types
// ============================================================================

type PillKind = "open" | "close";

type SerializedFunctionPillNode = Spread<
    {
        kind: PillKind;
        name: string;
        unclosed?: boolean;
    },
    SerializedLexicalNode
>;

const INVALID_FUNCTION_COLOR = "#000000";

/** Fired on a pill's own hover so the FunctionPillView with the matching pairId (its open/close
 * counterpart) can highlight itself too — see FunctionPillView. Not part of node state; pairId
 * itself lives only on the node instance (see FunctionPillNode.__pairId). */
const PAIR_HOVER_EVENT = "function-pill-pair-hover";

interface FunctionPillPairHoverDetail {
    pairId: string;
    hovered: boolean;
}

// ============================================================================
// FunctionPillView
// ============================================================================

interface FunctionPillViewProps {
    text: string;
    color: string;
    unclosed: boolean;
    pairId: string;
}

/**
 * Hovering either half of a function-call pair (the "name(" open pill or its ")" close pill)
 * underlines both in the primary color, like bracket-pair highlighting in a code editor — the two
 * pills are unrelated DOM nodes (no wrapper element groups them, see the module doc on
 * FunctionPillNode), so pairing is done via a shared `pairId` broadcast over a document-level
 * custom event rather than a CSS sibling selector.
 */
const FunctionPillView = ({ text, color, unclosed, pairId }: FunctionPillViewProps): ReactNode => {
    const [paired, setPaired] = useState(false);

    useEffect(() => {
        const handlePairHover = (event: Event) => {
            const detail = (event as CustomEvent<FunctionPillPairHoverDetail>).detail;
            if (detail.pairId !== pairId) return;
            setPaired(detail.hovered);
        };
        document.addEventListener(PAIR_HOVER_EVENT, handlePairHover);
        return () => document.removeEventListener(PAIR_HOVER_EVENT, handlePairHover);
    }, [pairId]);

    const dispatchPairHover = (hovered: boolean) => {
        document.dispatchEvent(
            new CustomEvent<FunctionPillPairHoverDetail>(PAIR_HOVER_EVENT, { detail: { pairId, hovered } })
        );
    };

    return (
        <span
            data-function-pill="true"
            data-function-pill-unclosed={unclosed || undefined}
            style={{ backgroundColor: color }}
            className={`inline-flex items-center px-2 py-0.5 rounded text-white text-sm mx-0.5 my-0.5 align-middle border-b-2 ${
                unclosed ? "border-red" : paired ? "border-primary" : "border-transparent"
            }`}
            contentEditable={false}
            onPointerEnter={() => dispatchPairHover(true)}
            onPointerLeave={() => dispatchPairHover(false)}
        >
            {text}
        </span>
    );
};

// ============================================================================
// FunctionPillNode
// ============================================================================

/**
 * Real, atomic DOM content for a FunctionCallNode's "name(" / ")" boundary — a DecoratorNode,
 * same as TagNode (minus the Edit/Copy/Remove popover, which doesn't make sense for a paren).
 * Earlier this was pure CSS (::before/::after generated content): visually identical, but with
 * nothing real for the browser to click into, arrow-key past, or anchor a caret near — every one
 * of those turned out broken. Real nodes give native click/arrow-key/caret behavior for free, the
 * same way it already works around TagNode pills.
 */
export class FunctionPillNode extends DecoratorNode<ReactNode> {
    __kind: PillKind;
    __name: string;
    __unclosed: boolean;
    __pairId: string;

    static getType(): string {
        return "function-pill";
    }

    static clone(node: FunctionPillNode): FunctionPillNode {
        return new FunctionPillNode(node.__kind, node.__name, node.__unclosed, node.__pairId, node.__key);
    }

    static importJSON(serializedNode: SerializedFunctionPillNode): FunctionPillNode {
        return $createFunctionPillNode(serializedNode.kind, serializedNode.name, {
            unclosed: serializedNode.unclosed,
        });
    }

    constructor(kind: PillKind, name: string, unclosed = false, pairId?: string, key?: NodeKey) {
        super(key);
        this.__kind = kind;
        this.__name = name;
        this.__unclosed = unclosed;
        this.__pairId = pairId ?? `${kind}:${name}:${key ?? ""}`;
    }

    exportJSON(): SerializedFunctionPillNode {
        return {
            ...super.exportJSON(),
            kind: this.__kind,
            name: this.__name,
            unclosed: this.__unclosed,
            type: "function-pill",
            version: 1,
        };
    }

    getTextContent(): string {
        return this.__kind === "open" ? `${this.__name}(` : ")";
    }

    isUnclosed(): boolean {
        return this.getLatest().__unclosed;
    }

    createDOM(): HTMLElement {
        const span = document.createElement("span");
        span.style.display = "inline-flex";
        span.style.alignItems = "center";
        span.style.verticalAlign = "middle";
        return span;
    }

    updateDOM(): false {
        return false;
    }

    isInline(): boolean {
        return true;
    }

    isKeyboardSelectable(): boolean {
        // See TagNode.isKeyboardSelectable — same reasoning: single-press skip over the pill
        // instead of a select-then-advance two-press stop, with Backspace/Delete unaffected.
        return false;
    }

    decorate(): ReactNode {
        const latest = this.getLatest();
        const paramType = getFunctionParamType(latest.__name);
        const color = paramType ? getTagPillColor(paramType) : INVALID_FUNCTION_COLOR;
        return (
            <FunctionPillView
                text={this.getTextContent()}
                color={color}
                unclosed={latest.__unclosed}
                pairId={latest.__pairId}
            />
        );
    }
}

// ============================================================================
// Factories
// ============================================================================

export const $createFunctionPillNode = (
    kind: PillKind,
    name: string,
    options?: { unclosed?: boolean; pairId?: string }
): FunctionPillNode => {
    return $applyNodeReplacement(new FunctionPillNode(kind, name, options?.unclosed ?? false, options?.pairId));
};

export const $isFunctionPillNode = (node: LexicalNode | null | undefined): node is FunctionPillNode => {
    return node instanceof FunctionPillNode;
};
